import { logErrorToSentry } from '../../Sentry';
import { deferred } from '../../util/deferred';
import { parse } from '../../util/parse';
import { Experiment } from '../types';

export const resolveClientExperiment = async (experiment: Experiment<unknown>, window: Window) => {
  const client = await getGlobalAdobeTargetClient(window);
  if (!client) return undefined;

  const value = await getOfferValue(client, experiment.mbox, experiment.parameters);

  try {
    return parse(value, experiment.validate);
  } catch (_error) {
    /** FIXME — when babel supports "catch(error: unknown)" syntax */
    const error: unknown = _error;
    logErrorToSentry(new Error(`Saw invalid data for mbox "${experiment.mbox}"`), { cause: error });
    return undefined;
  }
};

const getOfferValue = async (
  client: TargetClient,
  mbox: string,
  params?: Record<string, string>,
) => {
  try {
    const { promise, resolve, reject } = deferred<GetOfferResult>();

    client.getOffer?.({
      mbox: mbox,
      params: params,
      success: resolve,
      error: (status: unknown, error: unknown) => {
        reject({ status, error });
      },
    });

    const offer = await promise;

    return offer[0]?.content?.[0];
  } catch (_error) {
    /** FIXME — when babel supports "catch(error: unknown)" syntax */
    const error: unknown = _error;

    logErrorToSentry(new Error('Failed to get offer from Target'), {
      cause: error,
      mbox: mbox,
      params: params,
    });

    return undefined;
  }
};

interface ClientOffer {
  content?: (undefined | unknown)[];
}

type GetOfferResult = (ClientOffer | undefined)[];

interface TargetClient {
  getOffer?: (options: {
    params?: Record<string, string>;
    mbox: string;
    success: (offer: GetOfferResult) => void;
    error: (status: unknown, error: unknown) => void;
  }) => void;
}

declare global {
  interface Window {
    adobe?: {
      target?: TargetClient;
    };
  }
}

const getGlobalAdobeTargetClient = async (window: Window) => {
  const client = window.adobe?.target;

  if (client) {
    return client;
  }

  const promise = new Promise(resolve => {
    if (window.adobe) {
      resolve('');
      return;
    }

    Object.defineProperty(window, 'adobe', {
      configurable: true,
      set: (value: Window['adobe']) => {
        Object.defineProperty(window, 'adobe', {
          configurable: true,
          enumerable: true,
          writable: true,
          value: value,
        });

        resolve('');
      },
    });
  });

  await promise;

  return window.adobe?.target;
};
